import type { OpcionSelect } from "@/components";
import type { CotizacionDetalle, CuotaCotizacion, EscaneoFactura, Gasto, ItemDeFactura, PresupuestoVinculable, Vinculables } from "./api";
import { formatMoneda } from "./format";
import { aCentavos, deCentavos, formGasto, montoAInput, type FormGasto } from "./gastos";
import { SIN_VALOR } from "./proveedores";

/**
 * Facturas vinculadas a un presupuesto aceptado, al pago de su forma de pago que cubren y a un ítem del rubro
 * (docs/modulo-gastos.md § Facturas). En el form, "" o SIN_VALOR = sin vincular.
 */

export const vinculado = (valor: string): boolean => valor !== "" && valor !== SIN_VALOR;

export function presupuestoElegido(v: Vinculables | null, cotizacionId: string): PresupuestoVinculable | undefined {
  return vinculado(cotizacionId) ? v?.presupuestos.find((p) => String(p.id) === cotizacionId) : undefined;
}

/** Los aceptados del proveedor; si la factura ya tiene uno que dejó de estar aceptado, se conserva en la lista. */
export function opcionesPresupuesto(v: Vinculables | null, actual: Gasto["cotizacion"] = null): OpcionSelect[] {
  const lista = (v?.presupuestos ?? []).map((p) => ({ valor: String(p.id), etiqueta: p.descripcion ? `${p.nombre} · ${p.descripcion}` : p.nombre }));
  if (actual && !lista.some((o) => o.valor === String(actual.id))) lista.push({ valor: String(actual.id), etiqueta: `${actual.nombre} (ya no está aceptado)` });
  return lista.length ? [{ valor: SIN_VALOR, etiqueta: "Sin presupuesto" }, ...lista] : [];
}

/** "Anticipo · USD 7.000,00 · sin facturar USD 2.000,00". */
export function opcionesCuota(p: PresupuestoVinculable | undefined, actual: Gasto["cuota"] = null): OpcionSelect[] {
  if (!p || !p.cuotas.length) return actual ? [{ valor: SIN_VALOR, etiqueta: "Sin pago del plan" }, { valor: String(actual.id), etiqueta: actual.nombre }] : [];
  return [
    { valor: SIN_VALOR, etiqueta: "Sin pago del plan" },
    ...p.cuotas.map((c) => {
      if (c.monto_usd === null) return { valor: String(c.id), etiqueta: c.concepto };
      const falta = aCentavos(c.monto_usd) - aCentavos(c.facturado_usd);
      const resto = falta > 0n ? `sin facturar ${formatMoneda(deCentavos(falta), "USD")}` : "facturado entero";
      return { valor: String(c.id), etiqueta: `${c.concepto} · ${formatMoneda(c.monto_usd, "USD")} · ${resto}` };
    }),
  ];
}

/** Los ítems del rubro de la factura; primero los que cubre el presupuesto elegido. */
export function opcionesItem(items: ItemDeFactura[], rubroId: string, cubre: number[] = [], actual: Gasto["item"] = null): OpcionSelect[] {
  const delRubro = items.filter((i) => String(i.rubro_id) === rubroId);
  const orden = [...delRubro.filter((i) => cubre.includes(i.id)), ...delRubro.filter((i) => !cubre.includes(i.id))];
  const lista = orden.map((i) => ({ valor: String(i.id), etiqueta: i.nombre }));
  if (actual && String(rubroId) && !lista.some((o) => o.valor === String(actual.id))) lista.push({ valor: String(actual.id), etiqueta: actual.nombre });
  return lista.length ? [{ valor: SIN_VALOR, etiqueta: "Sin ítem" }, ...lista] : [];
}

/**
 * Cambia un campo del form de la factura con sus consecuencias: otro proveedor suelta el presupuesto y su pago; otro
 * presupuesto suelta el pago y, si cubre un solo rubro y no hay rubro elegido, lo propone; otro rubro suelta el ítem.
 */
export function cambiarCampoFactura<K extends keyof FormGasto>(f: FormGasto, campo: K, valor: FormGasto[K], v: Vinculables | null): FormGasto {
  const nuevo = { ...f, [campo]: valor };
  if (campo === "proveedor_id" && valor !== f.proveedor_id) return { ...nuevo, cotizacion_id: "", cuota_id: "" };
  if (campo === "cotizacion_id" && valor !== f.cotizacion_id) {
    const p = presupuestoElegido(v, valor as string);
    const rubro = p?.rubro_id && !vinculado(f.rubro_id) ? String(p.rubro_id) : f.rubro_id;
    return { ...nuevo, cuota_id: "", rubro_id: rubro, item_id: rubro !== f.rubro_id ? "" : f.item_id };
  }
  if (campo === "rubro_id" && valor !== f.rubro_id) return { ...nuevo, item_id: "" };
  return nuevo;
}

/** "Pérez SRL — Demolición · Anticipo" (para el listado y la ficha). */
export function textoPresupuesto(g: Pick<Gasto, "cotizacion" | "cuota">): string | null {
  if (!g.cotizacion) return null;
  return g.cuota ? `${g.cotizacion.nombre} · ${g.cuota.nombre}` : g.cotizacion.nombre;
}

const hayFacturado = (c: Pick<CuotaCotizacion, "facturado_usd">) => aCentavos(c.facturado_usd) > 0n;

/** Lo pagado de un pago del plan con sus facturas: "USD 3.500,00" y "Facturado USD 7.000,00"; null sin facturas. */
export function pagadoDeCuota(c: CuotaCotizacion): { pagado: string; detalle: string } | null {
  if (!hayFacturado(c)) return null;
  return { pagado: formatMoneda(c.pagado_usd, "USD"), detalle: `Facturado ${formatMoneda(c.facturado_usd, "USD")}` };
}

/** Antes de sacar o reemplazar pagos del plan que tienen facturas: las facturas siguen con el presupuesto, sin pago. */
export function avisoFacturasDelPlan(cuotas: CuotaCotizacion[]): string | null {
  return cuotas.some(hayFacturado) ? "Hay facturas vinculadas a estos pagos: quedan con el presupuesto, sin pago del plan." : null;
}

/** "Pagado USD 3.500,00 de USD 10.000,00 · falta USD 6.500,00"; pagado de más, lo dice. */
export function faltaPagar(d: Pick<CotizacionDetalle, "falta_pagar_usd">): string {
  if (d.falta_pagar_usd === null) return "Sin total";
  if (d.falta_pagar_usd.startsWith("-")) return `Pagado de más ${formatMoneda(d.falta_pagar_usd.slice(1), "USD")}`;
  return formatMoneda(d.falta_pagar_usd, "USD");
}

/** El listado filtrado por un presupuesto (`?presupuesto=<id>` desde su ficha): suma `cotizacion_id` al query. */
export function conPresupuesto(query: string, presupuestoId: string | null): string {
  if (!presupuestoId || !/^\d+$/.test(presupuestoId)) return query;
  return `${query ? `${query}&` : "?"}cotizacion_id=${presupuestoId}`;
}

/** El alta completada con lo que leyó Escanear factura; lo que no se leyó, como un alta nueva (hoy, ARS, tipo de cambio
 * sugerido). El presupuesto y su pago, los que propuso el backend; el ítem no se adivina. */
export function formDesdeEscaneo(e: EscaneoFactura, hoy: string, tcSugerido?: string | null): FormGasto {
  const nuevo = formGasto(undefined, hoy, tcSugerido);
  const id = (v: number | null) => (v === null ? "" : String(v));
  return {
    ...nuevo,
    fecha: e.fecha ?? nuevo.fecha,
    proveedor_id: id(e.proveedor_id),
    rubro_id: id(e.rubro_id),
    descripcion: e.descripcion ?? "",
    comprobante_nro: e.comprobante_nro ?? "",
    monto: e.monto ? montoAInput(e.monto) : "",
    moneda: e.moneda ?? nuevo.moneda,
    cotizacion_id: id(e.cotizacion_id),
    cuota_id: id(e.cuota_id),
  };
}
